import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { iRootDispatch, iRootState } from '../../store';
import SectionLabel from '../../components/SectionLabel';
import { Button, TextArea, Form, Grid, Icon } from 'semantic-ui-react';
import styled from 'styled-components';
import { Formik } from 'formik';
import { getUser, updateUser } from '../../services/user';
import {
  maskPhoneNumber,
  unmaskPhoneNumber,
  maskCpf,
  unmaskCpfOrCnpj
} from '../../services/masks';
import { successNotification, dangerNotification } from '../../services/notification';
import { FieldLabel } from '../../styles';
import { UserRolesEnum } from '../../enums/user-roles.enum';
import { UserStatusEnum } from '../../enums/user-status.enum';
import * as Yup from 'yup';
// Importa o método customizado de validação de email sem acentos
import '../fire-insurance-budget-form/validators/constants';
import { getBroker, getBrokerObjectFromGetBrokerDTO } from '../../services/broker';
import { BrokersToCustomDropDownOptions } from '../../util';
import { AvailableBrokersAndEstatesState } from '../../store/types/temp-types';
import { BrokerForGetBrokerResponseDTO } from '../../dtos/broker/broker-for-get-broker-response.dto';
import { BrokerState } from '../../types/BrokerState';
import { getEstateUnits } from '../../services/estate-unit';
import { EstateUnitStatusEnum } from '../../types/EstateUnit';
import { resolveUserEstateUnitId } from '../../utils/userEstateUnit.util';

const mapState = (state: iRootState) => ({
  availableBrokersAndEstates: state.availableBrokersAndEstates,
  user: state.user
});

const mapDispatch = (dispatch: iRootDispatch) => ({
  updateAvailableBrokersAndEstates: (availableBrokers: AvailableBrokersAndEstatesState) =>
    dispatch.availableBrokersAndEstates.updateAvailableBrokersAndEstates(availableBrokers)
});

type OptionsType = {
  value?: string;
  text: string;
  key: number;
};

const TextAreaWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 0 5px;

  > textarea {
    background-color: #f5f8fa;
    width: 135%;
    border-radius: 4px;
    border: 1px solid rgba(34, 36, 38, 0.15);
    padding: 10px 4px;
    outline: 0;

    &:focus {
      border-color: #85b7d9;
    }
  }

  > label {
    // font-weight: bold;
    margin-bottom: 5px;
  }
`;

export type Props = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

const statusOptions: OptionsType[] = [
  { key: 0, value: UserStatusEnum.active, text: 'Ativo' },
  { key: 1, value: UserStatusEnum.inactive, text: 'Inativo' },
  { key: 2, value: UserStatusEnum.pending, text: 'Pendente' }
];

const estateRoleOptions: OptionsType[] = [
  { key: 0, value: UserRolesEnum.estateAdmin, text: 'Administrador de Imobiliária' },
  { key: 1, value: UserRolesEnum.estateUser, text: 'Usuário de Imobiliária' },
  { key: 2, value: UserRolesEnum.estateQuotation, text: 'Cotação e consulta' }
];

const UsersForm: React.FC<Props> = (props: Props) => {
  const { role } = props.user;
  const [isLoading, setLoading] = useState<boolean>(false);
  const [loadedUser, setLoadedUser] = useState<any>({ id: undefined });
  const [isReady, setIsReady] = useState<boolean>(false);
  const [previousEstate, setPreviousEstate] = useState<string>('');
  const [previousFranchise, setPreviousFranchise] = useState<string>('');
  const [previousMasterFranchise, setPreviousMasterFranchise] = useState<string>('');

  const [franchiseOptions, setFranchiseOptions] = useState<OptionsType[]>(
    props.availableBrokersAndEstates.franchises
  );
  const [masterFranchiseOptions, setMasterFranchiseOptions] = useState<OptionsType[]>(
    props.availableBrokersAndEstates.multiFranchises
  );

  const [estateOptions, setEstateOptions] = useState<OptionsType[]>([]);

  const [selectedEstate, setSelectedEstate] = useState<string>('');
  const [selectedFranchise, setSelectedFranchise] = useState<string>('');
  const [selectedMasterFranchise, setSelectedMasterFranchise] = useState<string>('');

  const [userOptions, setUserOptions] = useState<OptionsType[]>([]);
  const [estateUnitOptions, setEstateUnitOptions] = useState<OptionsType[]>([]);

  const brokerUsers = [
    UserRolesEnum.brokerAdmin,
    UserRolesEnum.brokerAnalyst,
    UserRolesEnum.accountManager,
    UserRolesEnum.cancelationAnalyst,
    UserRolesEnum.claimAnalyst
  ];
  const estateUsers = [
    UserRolesEnum.estateAdmin,
    UserRolesEnum.estateUser,
    UserRolesEnum.estateQuotation
  ];
  const franchiseUsers = [UserRolesEnum.franchiseAdmin];
  const masterFranchiseUsers = [UserRolesEnum.multiFranchiseAdmin];

  const backOfficeEditors = [
    UserRolesEnum.brokerAdmin,
    UserRolesEnum.brokerAnalyst,
    UserRolesEnum.accountManager
  ];
  const isBackOfficeEditor = backOfficeEditors.includes(role as UserRolesEnum);

  const mapEstateOptions = () =>
    props.availableBrokersAndEstates.estates.map(estate => ({
      key: estate.key,
      text: estate.text,
      value: estate.value.id
    }));

  useEffect(() => {
    (async () => {
      const { id } = (props as any).location.state;

      if (!id) {
        return;
      }

      const user = await getUserById(id);
      if (!user) {
        return;
      }

      const dto = {
        id: user.id,
        name: user.name,
        email: user.email,
        estate: user.estate,
        franchise: user.franchise,
        multiFranchise: user.multiFranchise,
        role: user.role,
        cellphone: user.cellphone,
        doc: user.doc,
        status: user.status,
        observation: user.observation,
        estateId: user.estate?.id,
        estateUnitId: resolveUserEstateUnitId(user)
      };

      const estate = user.estate?.id || null;
      const franchise = user.franchise?.id || null;
      const multiFranchise = user.multiFranchise?.id || null;

      if (estate) {
        setPreviousEstate(String(estate));
        setSelectedEstate(String(estate));
      }

      if (franchise) {
        setPreviousFranchise(String(franchise));
        setFranchiseOptions([
          {
            key: user.franchise.id,
            text: user.franchise.name,
            value: user.franchise.id
          }
        ]);
        setSelectedFranchise(String(franchise));
      }

      if (multiFranchise) {
        setPreviousMasterFranchise(String(multiFranchise));
        setMasterFranchiseOptions([
          {
            key: user.multiFranchise.id,
            text: user.multiFranchise.responsibleName,
            value: user.multiFranchise.id
          }
        ]);
        setSelectedMasterFranchise(String(multiFranchise));
      }

      setLoadedUser(dto);
      setIsReady(true);
    })();
  }, [(props as any).location.state?.id]);

  useEffect(() => {
    if (!loadedUser.id || props.availableBrokersAndEstates.estates.length === 0) {
      return;
    }

    const estate = loadedUser.estate?.id || null;
    const franchise = loadedUser.franchise?.id || null;
    const multiFranchise = loadedUser.multiFranchise?.id || null;
    const allEstateOptions = mapEstateOptions();

    if (
      isBackOfficeEditor ||
      franchise ||
      multiFranchise ||
      (!franchise && !estate && !multiFranchise)
    ) {
      setEstateOptions(allEstateOptions);
      setFranchiseOptions(props.availableBrokersAndEstates.franchises);
      return;
    }

    if (estate && loadedUser.estate) {
      setEstateOptions([
        {
          key: loadedUser.estate.id,
          text: loadedUser.estate.name,
          value: loadedUser.estate.id
        }
      ]);
    }
  }, [
    loadedUser.id,
    loadedUser.estate,
    loadedUser.franchise,
    loadedUser.multiFranchise,
    props.availableBrokersAndEstates.estates,
    props.availableBrokersAndEstates.franchises,
    isBackOfficeEditor
  ]);

  useEffect(() => {
    if (userOptions.length === 0) {
      setLoading(true);
      const brokerOptions = props.availableBrokersAndEstates.roles.filter(role =>
        brokerUsers.includes(role.value as UserRolesEnum)
      );
      setUserOptions(brokerOptions);
      setLoading(false);
    }
  }, [userOptions]);

  const getUserById = async (id: string) => {
    setLoading(true);
    const searchUser = await getUser(id);

    setLoading(false);
    if (searchUser) {
      return searchUser;
    }
  };

  const update = async (payload: any) => {
    setLoading(true);
    try {
      const { id } = payload;
      if (payload.scope === 'estate') {
        if (!payload.estateId) {
          dangerNotification('Oops...', 'Selecione uma imobiliária');
          setLoading(false);
          return;
        }
      }

      if (payload.scope === 'franchise') {
        if (!payload.franchiseId) {
          dangerNotification('Oops...', 'Selecione uma franquia');
          setLoading(false);
          return;
        }
      }

      if (payload.scope === 'multiFranchise') {
        if (!payload.multiFranchiseId) {
          dangerNotification('Oops...', 'Selecione uma Multi Franquia');
          setLoading(false);
          return;
        }
      }

      payload = {
        ...payload,
        cellphone: payload.cellphone ? unmaskPhoneNumber(payload.cellphone) : null,
        doc: payload.doc ? unmaskCpfOrCnpj(payload.doc) : null,
        estateId: payload.estateId ? payload.estateId : null,
        franchiseId: payload.franchiseId ? payload.franchiseId : null,
        multiFranchiseId: payload.multiFranchiseId ? payload.multiFranchiseId : null,
        estateUnitId: payload.estateUnitId ? payload.estateUnitId : null,
        observation: payload.observation || null
      };

      delete payload.email;
      delete payload.scope;
      delete payload.estate;
      delete payload.franchise;
      delete payload.multiFranchise;

      await updateUser(id, payload);

      // TODO: refactor it and remove the duplicated logic
      const brokerDTO: BrokerForGetBrokerResponseDTO = await getBroker(
        '7c98d9d9-a975-45a3-9131-cc1961250182'
      );
      const broker: BrokerState | null = brokerDTO
        ? getBrokerObjectFromGetBrokerDTO(brokerDTO)
        : null;

      props.updateAvailableBrokersAndEstates({
        ...props.availableBrokersAndEstates,
        brokers: BrokersToCustomDropDownOptions(true, broker ? [broker] : [])
      });

      successNotification('Sucesso', 'Usuário editado com sucesso!');
      (props as any).history.push('/admin/users');
    } catch (error) {
      dangerNotification('Oops...', (error as any).message);
    }
    setLoading(false);
  };

  const checkEscope = (role: string) => {
    if (estateUsers.includes(role as UserRolesEnum)) {
      return 'estate';
    } else if (franchiseUsers.includes(role as UserRolesEnum)) {
      return 'franchise';
    } else if (masterFranchiseUsers.includes(role as UserRolesEnum)) {
      return 'multiFranchise';
    } else if (brokerUsers.includes(role as UserRolesEnum)) {
      return 'Confiax';
    } else {
      return '';
    }
  };

  const userEstate = loadedUser.role
    ? loadedUser.role.includes('ESTATE')
      ? loadedUser.estate
        ? loadedUser.estate
        : undefined
      : null
    : '';
  const userFranchise = loadedUser.role
    ? loadedUser.role.includes('FRANCHISE')
      ? loadedUser.franchise
        ? loadedUser.franchise
        : undefined
      : null
    : '';

  const userMasterFranchise = loadedUser.role
    ? loadedUser.role.includes('MULTI_FRANCHISE')
      ? loadedUser.multiFranchise
        ? loadedUser.multiFranchise
        : undefined
      : null
    : '';

  const userScope = loadedUser.role ? checkEscope(loadedUser.role) : '';

  const initialValuesUserForm = {
    id: loadedUser.id,
    name: loadedUser.name,
    email: loadedUser.email,
    role: loadedUser.role,
    estate: userEstate,
    franchise: userFranchise,
    multiFranchise: userMasterFranchise,
    doc: typeof loadedUser.doc === 'string' ? maskCpf(loadedUser.doc) : loadedUser.doc,
    cellphone:
      typeof loadedUser.cellphone === 'string'
        ? maskPhoneNumber(loadedUser.cellphone)
        : loadedUser.cellphone,
    observation: loadedUser.observation,
    status: loadedUser.status || UserStatusEnum.active,
    scope: userScope,
    estateId: userEstate?.id || '',
    franchiseId: userFranchise?.id || '',
    multiFranchiseId: userMasterFranchise?.id || '',
    estateUnitId: resolveUserEstateUnitId(loadedUser)
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Campo Obrigatório'),
    email: Yup.string().isValidEmailWithoutAccents().required('Campo Obrigatório'),
    role: Yup.string().required('Campo Obrigatório'),
    estate: Yup.string(),
    status: Yup.string(),
    cellphone: Yup.string().matches(
      /^\([0-9]{2}\) [0-9]{4,5}-[0-9]{4}$/,
      'Telefone inválido'
    ),
    doc: Yup.string().matches(/^[0-9]{3}.[0-9]{3}.[0-9]{3}-[0-9]{2}$/, 'CPF inválido'),
    observation: Yup.string()
  });

  return (
    <>
      {isReady && (
        <Formik
          initialValues={initialValuesUserForm}
          validationSchema={validationSchema}
          onSubmit={() => {}}
        >
          {formikProps => (
            <UsersEditFormFields
              {...formikProps}
              role={role}
              isBackOfficeEditor={isBackOfficeEditor}
              isLoading={isLoading}
              userOptions={userOptions}
              estateOptions={estateOptions}
              estateUnitOptions={estateUnitOptions}
              setEstateUnitOptions={setEstateUnitOptions}
              franchiseOptions={franchiseOptions}
              masterFranchiseOptions={masterFranchiseOptions}
              selectedEstate={selectedEstate}
              setSelectedEstate={setSelectedEstate}
              selectedFranchise={selectedFranchise}
              setSelectedFranchise={setSelectedFranchise}
              selectedMasterFranchise={selectedMasterFranchise}
              setSelectedMasterFranchise={setSelectedMasterFranchise}
              previousEstate={previousEstate}
              setPreviousEstate={setPreviousEstate}
              previousFranchise={previousFranchise}
              setPreviousFranchise={setPreviousFranchise}
              previousMasterFranchise={previousMasterFranchise}
              setPreviousMasterFranchise={setPreviousMasterFranchise}
              update={update}
              assignedEstateUnitName={
                (loadedUser as any)?.estate_unit?.name ||
                (loadedUser as any)?.estateUnit?.name ||
                ''
              }
            />
          )}
        </Formik>
      )}
    </>
  );
};

type UserEditFormValues = {
  name: string;
  email: string;
  role: string;
  doc: string;
  cellphone: string;
  observation: string;
  status: string;
  scope: string;
  estateId: string;
  franchiseId: string;
  multiFranchiseId: string;
  estateUnitId: string;
};

type UsersEditFormFieldsProps = {
  values: UserEditFormValues;
  setFieldValue: (field: string, value: unknown) => void;
  role: string;
  isBackOfficeEditor: boolean;
  isLoading: boolean;
  userOptions: OptionsType[];
  estateOptions: OptionsType[];
  estateUnitOptions: OptionsType[];
  setEstateUnitOptions: React.Dispatch<React.SetStateAction<OptionsType[]>>;
  franchiseOptions: OptionsType[];
  masterFranchiseOptions: OptionsType[];
  selectedEstate: string;
  setSelectedEstate: React.Dispatch<React.SetStateAction<string>>;
  selectedFranchise: string;
  setSelectedFranchise: React.Dispatch<React.SetStateAction<string>>;
  selectedMasterFranchise: string;
  setSelectedMasterFranchise: React.Dispatch<React.SetStateAction<string>>;
  previousEstate: string;
  setPreviousEstate: React.Dispatch<React.SetStateAction<string>>;
  previousFranchise: string;
  setPreviousFranchise: React.Dispatch<React.SetStateAction<string>>;
  previousMasterFranchise: string;
  setPreviousMasterFranchise: React.Dispatch<React.SetStateAction<string>>;
  update: (payload: Record<string, unknown>) => Promise<void>;
  assignedEstateUnitName: string;
};

const UsersEditFormFields: React.FC<UsersEditFormFieldsProps> = ({
  values,
  setFieldValue,
  role,
  isBackOfficeEditor,
  isLoading,
  userOptions,
  estateOptions,
  estateUnitOptions,
  setEstateUnitOptions,
  franchiseOptions,
  masterFranchiseOptions,
  selectedEstate,
  setSelectedEstate,
  selectedFranchise,
  setSelectedFranchise,
  selectedMasterFranchise,
  setSelectedMasterFranchise,
  previousEstate,
  setPreviousEstate,
  previousFranchise,
  setPreviousFranchise,
  previousMasterFranchise,
  setPreviousMasterFranchise,
  update,
  assignedEstateUnitName
}) => {
  const history = useHistory();

  useEffect(() => {
    const estateId = values.estateId ? String(values.estateId) : '';

    if (!estateId || values.scope !== 'estate') {
      setEstateUnitOptions([]);
      return;
    }

    const currentUnitId = values.estateUnitId ? String(values.estateUnitId) : '';
    const currentUnitName = assignedEstateUnitName || 'Unidade atribuída';

    getEstateUnits({ estateId, status: EstateUnitStatusEnum.active })
      .then(units => {
        const options = (units || []).map((unit, index) => ({
          key: index,
          text: unit.name,
          value: unit.id
        }));

        if (
          currentUnitId &&
          !options.some(option => String(option.value) === currentUnitId)
        ) {
          options.unshift({
            key: -1,
            text: currentUnitName,
            value: currentUnitId
          });
        }

        setEstateUnitOptions(options);
      })
      .catch(() => setEstateUnitOptions([]));
  }, [values.estateId, values.scope, values.estateUnitId, assignedEstateUnitName, setEstateUnitOptions]);

  return (
    <Form>
      <Grid>
        <Grid.Row>
          <Grid.Column>
            <Button basic onClick={() => history.push('/admin/users')}>
              <Icon name="arrow left" />
              Voltar
            </Button>
          </Grid.Column>
        </Grid.Row>
      </Grid>

      <SectionLabel text={'DADOS DO USUÁRIO'} />
              {(role === UserRolesEnum.brokerAdmin ||
                role === UserRolesEnum.brokerAnalyst ||
                role === UserRolesEnum.accountManager) && (
                <>
                  <Form.Group>
                    <Form.Radio
                      name={'scope'}
                      label={'Corretora de Seguros RV'}
                      value={values.scope}
                      checked={values.scope === 'Confiax'}
                      onChange={(e, data) => {
                        setFieldValue('scope', 'Confiax');
                        setFieldValue('estate', null);
                        setFieldValue('estateId', null);
                        setFieldValue('estateUnitId', null);
                        setFieldValue('franchise', null);
                      }}
                    />

                    <Form.Radio
                      name={'scope'}
                      label={'Imobiliária'}
                      value={values.scope}
                      checked={values.scope === 'estate'}
                      onChange={(e, data) => {
                        setFieldValue('scope', 'estate');
                        setFieldValue('role', UserRolesEnum.estateUser);
                        setFieldValue('estate', previousEstate);
                        setFieldValue('estateId', previousEstate || '');
                        setSelectedEstate(previousEstate || '');
                        setFieldValue('franchise', null);
                        setFieldValue('franchiseId', null);
                      }}
                    />

                    <Form.Radio
                      name={'scope'}
                      label={'Franquia'}
                      value={values.scope}
                      checked={values.scope === 'franchise'}
                      onChange={(e, data) => {
                        setFieldValue('scope', 'franchise');
                        setFieldValue('role', UserRolesEnum.franchiseAdmin);
                        setFieldValue('franchise', previousFranchise);
                        setFieldValue('franchiseId', previousFranchise || '');
                        setSelectedFranchise(previousFranchise || '');
                        setFieldValue('estate', null);
                        setFieldValue('estateId', null);
                      }}
                    />

                    <Form.Radio
                      name={'scope'}
                      label={'Multi Franquia'}
                      value={values.scope}
                      checked={values.scope === 'multiFranchise'}
                      onChange={(e, data) => {
                        setFieldValue('scope', 'multiFranchise');
                        setFieldValue('role', UserRolesEnum.multiFranchiseAdmin);
                        setFieldValue('multiFranchise', previousMasterFranchise);
                        setFieldValue('multiFranchiseId', previousMasterFranchise || '');
                        setSelectedMasterFranchise(previousMasterFranchise || '');
                        setFieldValue('estate', null);
                        setFieldValue('estateId', null);
                        setFieldValue('franchise', null);
                        setFieldValue('franchiseId', null);
                      }}
                    />
                  </Form.Group>
                </>
              )}
              <Form.Group widths={'equal'}>
                {values.scope === 'Confiax' ? (
                  <Form.Select
                    name={'role'}
                    options={userOptions}
                    label={'Tipo de Usuário'}
                    loading={isLoading}
                    clearable={true}
                    value={values.role}
                    onChange={(e, data) => setFieldValue('role', data.value)}
                  />
                ) : values.scope === 'estate' ? (
                  <>
                    <Form.Select
                      name={'estate'}
                      options={estateOptions}
                      disabled={!isBackOfficeEditor && estateOptions.length === 1}
                      label={'Imobiliária'}
                      search
                      onChange={(e, data) => {
                        setSelectedEstate(String(data.value));
                        setFieldValue('estateId', String(data.value));
                        setFieldValue('estateUnitId', '');
                        setPreviousEstate(String(data.value));
                      }}
                      value={selectedEstate}
                    />
                    <Form.Select
                      name={'role'}
                      options={estateRoleOptions}
                      label={'Tipo de Usuário'}
                      value={values.role}
                      onChange={(e, data) => setFieldValue('role', data.value)}
                    />
                    {estateUnitOptions.length > 0 && (
                      <Form.Select
                        name="estateUnitId"
                        options={estateUnitOptions}
                        label="Unidade"
                        search
                        clearable
                        value={values.estateUnitId || ''}
                        onChange={(_, data) =>
                          setFieldValue('estateUnitId', data.value || '')
                        }
                      />
                    )}
                  </>
                ) : values.scope === 'franchise' ? (
                  <Form.Select
                    name={'franchise'}
                    options={franchiseOptions}
                    disabled={franchiseOptions.length === 1}
                    label={'Franquia'}
                    onChange={(e, data) => {
                      setSelectedFranchise(String(data.value));
                      setFieldValue('franchiseId', String(data.value));
                    }}
                    value={selectedFranchise}
                  />
                ) : (
                  <Form.Select
                    name={'multiFranchise'}
                    options={masterFranchiseOptions}
                    label={'Multi Franquia'}
                    onChange={(e, data) => {
                      setSelectedMasterFranchise(String(data.value));
                      setFieldValue('multiFranchiseId', String(data.value));
                    }}
                    value={selectedMasterFranchise}
                  />
                )}
                <Form.Input
                  name={'name'}
                  label={'Nome'}
                  value={values.name}
                  onChange={e => setFieldValue('name', e.target.value)}
                />
                <Form.Input
                  name={'email'}
                  label={'E-mail'}
                  readonly={true}
                  value={values.email}
                  onChange={e => setFieldValue('email', e.target.value)}
                  readOnly
                />
              </Form.Group>

              <Form.Group widths={'equal'}>
                <Form.Input
                  name={'doc'}
                  label={'CPF'}
                  value={values.doc}
                  onChange={e => setFieldValue('doc', maskCpf(e.target.value))}
                />

                <Form.Input
                  name={'cellphone'}
                  label={'Celular'}
                  value={values.cellphone}
                  onChange={e =>
                    setFieldValue('cellphone', maskPhoneNumber(e.target.value))
                  }
                />

                <Form.Select
                  name={'status'}
                  options={statusOptions}
                  disabled={
                    values.status === 'Confiax' ? true : statusOptions.length === 1
                  }
                  label={'Status'}
                  loading={isLoading}
                  clearable={true}
                  value={
                    statusOptions.length === 1 ? statusOptions[0].value : values.status
                  }
                  onChange={(e, data) => setFieldValue('status', data.value)}
                />
              </Form.Group>
              <Form.Group widths={'equal'}>
                <TextAreaWrapper>
                  <FieldLabel>Observações</FieldLabel>
                  <TextArea
                    value={values.observation}
                    onChange={(e, data) => setFieldValue('observation', data.value)}
                  />
                </TextAreaWrapper>
              </Form.Group>

              <Button onClick={() => update(values)} color="green">
                Editar
              </Button>
              <Button onClick={() => history.push('/admin/users')}>
                Voltar
              </Button>
            </Form>
  );
};

export default connect(mapState, mapDispatch)(UsersForm);
